import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { UNSUPPORTED_FIELD_TYPES_IN_GIC } from '@atlassian/jira-issue-create-common-types/src/common/constants/index.tsx';
import type {
	FieldTabs,
	FieldTab,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { transformStargateUrl } from '@atlassian/jira-issue-create-commons/src/common/utils/transform-stargate-url/index.tsx';
import type { TimeTrackingConfig } from '@atlassian/jira-issue-format-time/src/index.tsx';
import { PROJECT_PICKER_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { transformToUI } from '@atlassian/jira-platform-utils-number';
import { getGqlFieldMap, CONTEXT_FIELD_TYPES } from '../../constants/field-map.tsx';
import {
	PROJECT_FIELD,
	ISSUE_TYPE_FIELD,
	REQUEST_TYPE_FIELD,
	NUMBER_FIELD,
	ISSUE_LINK_FIELD,
} from '../../constants/graphql-field-types.tsx';
import type {
	ProformaConfigurationService,
	WatchersConfigurationService,
	CreateLayoutItem,
	FieldConfigData,
	FieldsKeyedById,
	RequestTypeIssueTypeWithName,
	TransformedQueryData,
	CreateConfigQueryResponse as CreateConfigQueryResponseOld,
} from '../../types/index.tsx';
import {
	transformProjectField,
	transformIssueTypeField,
	transformRequestTypeField,
} from '../context-fields/index.tsx';
import { getGqlUnsupportedFields, getQueryErrorMessage } from '../graphql-error-utils/index.tsx';
import { overrideGqlTypes } from '../type-overrides/index.tsx';
import type {
	TimeTrackingSettings,
	CreateConfigQuery,
	CreateConfigQueryInContext,
	CreateConfigQueryResponse,
	CreateSubtaskConfigQuery,
	AdditionalServiceNode,
	JiraIssueFieldUnion,
	JiraQuery,
} from '../../types/create-config-query.tsx';
import type { ContextFieldsMap } from '../../types/context-fields.tsx';

type GetTabLayoutInfoParams = {
	data: TransformedQueryData;
	onlyRequiredField?: boolean;
	allFieldIds: string[];
};

export type TransformedQueryResult = {
	fieldTabs: FieldTabs;
	meta: {
		areRequiredFieldsSupported: boolean;
		queryErrorMessage: string;
	};
	additionalServices?: {
		watchersConfigurations?: WatchersConfigurationService['permissions'];
		proformaConfiguration?: ProformaConfigurationService['proforma'];
		storageLimitsData?: JiraQuery | null;
	};
} & FieldsKeyedById;

type FieldKeysWithoutConfig = Omit<JiraIssueFieldUnion, 'fieldConfig'>;

export const getTabLayoutInfo = ({
	data,
	onlyRequiredField = false,
	allFieldIds = [],
}: GetTabLayoutInfoParams): FieldTabs => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const fieldTabs = get(
		data,
		'jiraIssueCreateLayout.contentSection.items',
		[],
	) as CreateLayoutItem[];

	return fieldTabs
		.map(({ id, name, items, collapsedItems, tabId }) => {
			const filterItems = (itemsToFilter: CreateLayoutItem['items']) =>
				itemsToFilter
					.filter(({ fieldId }) => allFieldIds.includes(fieldId))
					.map(({ fieldId }) => fieldId);

			const filteredItems = filterItems(items || []);
			const filteredCollapsedItems = filterItems(collapsedItems || []);

			const result: FieldTab = {
				id,
				name,
				items: filteredItems,
				tabId,
			};

			result.collapsedItems = filteredCollapsedItems;
			if (onlyRequiredField && result.items.length === 0 && result.collapsedItems.length === 0)
				return null;

			return result;
		})
		.filter((tab): tab is FieldTab => !!tab);
};

const lowerCaseFormatMap = {
	PRETTY: 'pretty',
	HOURS: 'hours',
	DAYS: 'days',
} as const;

const lowerCaseUnitMap = {
	MINUTE: 'minute',
	HOUR: 'hour',
	DAY: 'day',
	WEEK: 'week',
} as const;

export const transformTimeTrackingSettings = (
	timeTrackingSettings?: TimeTrackingSettings,
): TimeTrackingConfig | undefined => {
	if (timeTrackingSettings) {
		const {
			defaultFormat,
			workingDaysPerWeek,
			workingHoursPerDay,
			defaultUnit,
			isJiraConfiguredTimeTrackingEnabled,
		} = timeTrackingSettings;

		const format = lowerCaseFormatMap[defaultFormat] ?? 'pretty';
		const unit = lowerCaseUnitMap[defaultUnit] ?? 'week';

		return {
			daysPerWeek: workingDaysPerWeek,
			hoursPerDay: workingHoursPerDay,
			format,
			defaultUnit: unit,
			isTimeTrackingEnabled: isJiraConfiguredTimeTrackingEnabled,
		};
	}
	return undefined;
};

export const getFieldsKeyedById = (
	data: TransformedQueryData,
	onlyRequiredField = false,
): FieldsKeyedById => {
	return fg('gic-typing-improvements')
		? getFieldsKeyedByIdNew(data, onlyRequiredField)
		: // @ts-expect-error - argument is functionally identical to pre-refactor, error will be resolved after feature flag is rolled out
			getFieldsKeyedByIdOld(data, onlyRequiredField);
};

// TODO: typing has been improved, but this really needs to be refactored further so that inclusion of field-specific types is
// behind a type guard on the field's __typename, rather checking for the presence of each field every loop.
const getFieldsKeyedByIdNew = (
	data: TransformedQueryData,
	onlyRequiredField = false,
): FieldsKeyedById => {
	const fields = data.jiraIssueCreateFields.fields?.nodes ?? [];
	const contextFields: ContextFieldsMap = {};
	const fieldConfigData: Record<string, FieldConfigData> = {};
	const requiredFieldConfigData: Record<string, FieldConfigData> = {};
	let isRequiredFieldComponentSupported = true;
	let gqlTypeBeingProcessed: string;
	fields.forEach((fieldData) => {
		try {
			const { __typename: gqlType, fieldId, name, type, description, fieldConfig } = fieldData;

			const mediaContext = 'mediaContext' in fieldData ? fieldData.mediaContext : undefined;
			const adminRichTextConfig =
				'adminRichTextConfig' in fieldData ? fieldData.adminRichTextConfig : undefined;
			const isMulti = 'isMulti' in fieldData ? fieldData.isMulti : undefined;
			const attributesIncludedInAutoCompleteSearch =
				'attributesIncludedInAutoCompleteSearch' in fieldData
					? fieldData.attributesIncludedInAutoCompleteSearch
					: undefined;
			const timeTrackingSettings =
				'timeTrackingSettings' in fieldData ? fieldData.timeTrackingSettings : undefined;
			const maxAllowedTotalAttachmentsSize =
				'maxAllowedTotalAttachmentsSize' in fieldData
					? fieldData.maxAllowedTotalAttachmentsSize
					: undefined;
			const searchUrl = 'searchUrl' in fieldData ? fieldData.searchUrl : undefined;

			gqlTypeBeingProcessed = gqlType;
			if (fieldId && !UNSUPPORTED_FIELD_TYPES_IN_GIC.includes(type)) {
				if (CONTEXT_FIELD_TYPES.includes(gqlType) && type !== PROJECT_PICKER_CF_TYPE) {
					let transformedData;
					if (gqlType === PROJECT_FIELD) {
						transformedData = transformProjectField(fieldData);
						contextFields[fieldId] = transformedData;
					}
					if (gqlType === ISSUE_TYPE_FIELD) {
						transformedData = transformIssueTypeField(fieldData);
						contextFields[fieldId] = transformedData;
					}
					if (gqlType === REQUEST_TYPE_FIELD) {
						const requestTypeFieldData: RequestTypeIssueTypeWithName = fieldData;
						if (requestTypeFieldData.requestType?.issueType) {
							requestTypeFieldData.requestType.issueType.issueTypeName =
								// @ts-expect-error - issuetype should always be of type TransformedIssueField, so this should be safe, but TS does not support negated types so we can't update the type to reflect this.
								// adding contextFields.issuetype?.__typename === ISSUE_TYPE_FIELD ensures type safety, but I'm not familiar enough with GIRA to know if this is always true.
								contextFields.issuetype.selectedIssueType?.label;
						}
						transformedData = transformRequestTypeField(requestTypeFieldData);
						contextFields.requestType = transformedData;
					}
				} else {
					const gqlTypeWithOverride = overrideGqlTypes(fieldData);
					const gqlFieldMap = getGqlFieldMap();
					const isValidGqlFieldMapKey = (value: string): value is keyof typeof gqlFieldMap => {
						return value in gqlFieldMap;
					};

					const isValidFieldDataKey = (key: string): key is keyof FieldKeysWithoutConfig => {
						return !!get(fieldData, key);
					};

					const gqlFieldConfig: (typeof gqlFieldMap)[keyof typeof gqlFieldMap] | {} =
						isValidGqlFieldMapKey(gqlTypeWithOverride) ? gqlFieldMap[gqlTypeWithOverride] : {};

					const defaultValueKey =
						gqlFieldConfig && 'defaultValueKey' in gqlFieldConfig
							? gqlFieldConfig.defaultValueKey
							: undefined;
					const allowedValuesKey =
						gqlFieldConfig && 'allowedValuesKey' in gqlFieldConfig
							? gqlFieldConfig.allowedValuesKey
							: undefined;

					let defaultValue: string | (string | null | false | undefined)[] | undefined =
						Array.isArray(defaultValueKey)
							? defaultValueKey.map(
									(key: string) => key && isValidFieldDataKey(key) && get(fieldData, key),
								)
							: (defaultValueKey &&
									isValidFieldDataKey(defaultValueKey) &&
									get(fieldData, defaultValueKey)) ||
								undefined;

					if (gqlType === NUMBER_FIELD && fg('frontier-currency-field-fix-percentage-storage')) {
						const transformedValue = transformToUI(
							Number(defaultValue),
							fieldData.formatConfig?.formatStyle,
						);
						if (fieldData.number === 0 && fg('fixing-number-field-default-value')) {
							defaultValue = '0';
						} else defaultValue = transformedValue ? String(transformedValue) : undefined;
					}

					const allowedValues =
						allowedValuesKey && isValidFieldDataKey(allowedValuesKey)
							? get(fieldData, allowedValuesKey)
							: undefined;

					const isRequired = fieldConfig?.isRequired || false;
					const isEditable = fieldConfig?.isEditable;
					const nonEditableReason = fieldConfig?.nonEditableReason || {};
					const copyLinksAndAttachments =
						gqlType === ISSUE_LINK_FIELD && fg('create_linked_issues_trigger_point_issue_view')
							? fieldData.copyLinksAndAttachments
							: undefined;

					fieldConfigData[fieldId] = {
						gqlType: gqlTypeWithOverride,
						fieldId,
						fieldName: name,
						fieldType: type,
						description: description || null,
						isRequired,
						isEditable,
						nonEditableReason,
						autoCompleteUrl: transformStargateUrl(searchUrl || ''),
						defaultValue: isNil(defaultValue) ? undefined : defaultValue,
						allowedValues,
						additionalData: {
							mediaContext,
							isMulti,
							attributesIncludedInAutoCompleteSearch,
							timeTrackingSettings: transformTimeTrackingSettings(timeTrackingSettings),
							maxAllowedTotalAttachmentsSize,
							...(fg('create_linked_issues_trigger_point_issue_view')
								? { copyLinksAndAttachments }
								: {}),
						},
					};
					if (adminRichTextConfig) {
						fieldConfigData[fieldId].additionalData.adminRichTextConfig = adminRichTextConfig;
					}

					if (onlyRequiredField && isRequired) {
						requiredFieldConfigData[fieldId] = fieldConfigData[fieldId];
					}
					if (isRequired && !(gqlFieldConfig && 'Component' in gqlFieldConfig)) {
						isRequiredFieldComponentSupported = false;
					}

					if (gqlType === NUMBER_FIELD && fg('number-format-in-gic')) {
						fieldConfigData[fieldId].formatConfig = fieldData.formatConfig;
					}
				}
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'processingApiFieldData',
					packageName: 'jiraIssueCreate',
					teamName: 'gryffindor',
				},
				error,
				attributes: {
					error: `processing ${gqlTypeBeingProcessed} failed`,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	});

	// process fields which are not supported in graphql layer
	const errors = data.jiraIssueCreateFields.errors || [];
	const { unsupportedFields, isRequiredFieldTypeSupported } = getGqlUnsupportedFields(errors);
	return {
		fieldConfig: onlyRequiredField
			? { ...requiredFieldConfigData }
			: { ...fieldConfigData, ...unsupportedFields },
		contextFields,
		meta: {
			areRequiredFieldsSupported: isRequiredFieldTypeSupported && isRequiredFieldComponentSupported,
		},
	};
};

const getFieldsKeyedByIdOld = (
	data: CreateConfigQueryResponseOld,
	onlyRequiredField = false,
): FieldsKeyedById => {
	const fields = get(data, 'jiraIssueCreateFields.fields.nodes', []);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const contextFields: Record<string, any> = {};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const fieldConfigData: Record<string, any> = {};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const requiredFieldConfigData: Record<string, any> = {};
	let isRequiredFieldComponentSupported = true;
	let gqlTypeBeingProcessed: string;
	fields.forEach((fieldData) => {
		try {
			const {
				__typename: gqlType,
				fieldId,
				name,
				type,
				description,
				fieldConfig = {},
				searchUrl,
				mediaContext,
				adminRichTextConfig,
				isMulti,
				attributesIncludedInAutoCompleteSearch,
				timeTrackingSettings,
				maxAllowedTotalAttachmentsSize,
			} = fieldData;

			gqlTypeBeingProcessed = gqlType;
			if (fieldId && !UNSUPPORTED_FIELD_TYPES_IN_GIC.includes(type)) {
				if (CONTEXT_FIELD_TYPES.includes(gqlType) && type !== PROJECT_PICKER_CF_TYPE) {
					let transformedData;
					if (gqlType === PROJECT_FIELD) {
						transformedData = transformProjectField(fieldData);
						contextFields[fieldId] = transformedData;
					}
					if (gqlType === ISSUE_TYPE_FIELD) {
						transformedData = transformIssueTypeField(fieldData);
						contextFields[fieldId] = transformedData;
					}
					if (gqlType === REQUEST_TYPE_FIELD) {
						const requestTypeFieldData = fieldData;
						// @ts-expect-error - TS2339 Property 'requestType' does not exist on type 'never'.
						if (requestTypeFieldData?.requestType?.issueType) {
							// @ts-expect-error - TS2339 Property 'requestType' does not exist on type 'never'.
							requestTypeFieldData.requestType.issueType.issueTypeName =
								contextFields?.issuetype?.selectedIssueType?.label;
						}
						transformedData = transformRequestTypeField(requestTypeFieldData);
						contextFields.requestType = transformedData;
					}
				} else {
					const gqlTypeWithOverride = overrideGqlTypes(fieldData);
					const gqlFieldConfig =
						// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly JiraAffectedServicesField: { readonly Component: AbstractComponent<ReturnComponentProps<AffectedServicesFieldConfig>, any>; readonly defaultValueKey: "selectedAffectedServices"; readonly submitTransform: (options: AffectedServiceOption[]) => ServiceEntity[]; readonly retainUserInputTransform: (selectedAff...'.
						getGqlFieldMap()[gqlTypeWithOverride] || {};
					const { defaultValueKey, allowedValuesKey } = gqlFieldConfig;

					let defaultValue;
					if (Array.isArray(defaultValueKey)) {
						defaultValue = defaultValueKey.map(
							(key: string) => key && get(fieldData, key, undefined),
						);
					} else {
						defaultValue = defaultValueKey && get(fieldData, defaultValueKey, undefined);
					}

					const allowedValues = allowedValuesKey && get(fieldData, allowedValuesKey, undefined);
					// @ts-expect-error - TS2339 Property 'isRequired' does not exist on type '{}'.
					const isRequired = fieldConfig?.isRequired || false;
					// @ts-expect-error - TS2339 Property 'isEditable' does not exist on type '{}'.
					const isEditable = fieldConfig?.isEditable;
					// @ts-expect-error - TS2339 Property 'nonEditableReason' does not exist on type '{}'.
					const nonEditableReason = fieldConfig?.nonEditableReason || {};
					const copyLinksAndAttachments =
						gqlTypeWithOverride === ISSUE_LINK_FIELD &&
						fg('create_linked_issues_trigger_point_issue_view')
							? // @ts-expect-error - TS2339 Property 'copyLinksAndAttachments' does not exist on type 'never'.
								fieldData?.copyLinksAndAttachments
							: undefined;

					fieldConfigData[fieldId] = {
						gqlType: gqlTypeWithOverride,
						fieldId,
						fieldName: name,
						fieldType: type,
						description,
						isRequired,
						isEditable,
						nonEditableReason,
						autoCompleteUrl: transformStargateUrl(searchUrl || ''),
						defaultValue: isNil(defaultValue) ? undefined : defaultValue,
						allowedValues,
						additionalData: {
							mediaContext,
							isMulti,
							attributesIncludedInAutoCompleteSearch,
							timeTrackingSettings: transformTimeTrackingSettings(timeTrackingSettings),
							maxAllowedTotalAttachmentsSize,
							...(fg('create_linked_issues_trigger_point_issue_view')
								? { copyLinksAndAttachments }
								: {}),
						},
					};
					if (adminRichTextConfig) {
						fieldConfigData[fieldId].additionalData.adminRichTextConfig = adminRichTextConfig;
					}

					if (onlyRequiredField && isRequired) {
						requiredFieldConfigData[fieldId] = fieldConfigData[fieldId];
					}
					if (isRequired && !('Component' in gqlFieldConfig)) {
						isRequiredFieldComponentSupported = false;
					}

					if (gqlType === NUMBER_FIELD && fg('number-format-in-gic')) {
						// @ts-expect-error - TS2339 Property 'formatConfig' does not exist on type '{}'.
						fieldConfigData[fieldId].formatConfig = fieldConfig.formatConfig;
					}
				}
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'processingApiFieldData',
					packageName: 'jiraIssueCreate',
					teamName: 'gryffindor',
				},
				error,
				attributes: {
					error: `processing ${gqlTypeBeingProcessed} failed`,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	});

	// process fields which are not supported in graphql layer
	const errors = get(data, 'jiraIssueCreateFields.errors', []);
	const { unsupportedFields, isRequiredFieldTypeSupported } = getGqlUnsupportedFields(errors);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return {
		fieldConfig: onlyRequiredField
			? { ...requiredFieldConfigData }
			: { ...fieldConfigData, ...unsupportedFields },
		contextFields,
		meta: {
			areRequiredFieldsSupported: isRequiredFieldTypeSupported && isRequiredFieldComponentSupported,
		},
	} as FieldsKeyedById;
};

export const getServicesKeyedByServiceId = (data: TransformedQueryData) => {
	const nodes: { node: AdditionalServiceNode }[] = get(
		data,
		'jiraIssueCreateAdditionalServices.services.edges',
		[],
	);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const services = {} as Record<AdditionalServiceNode['serviceId'], AdditionalServiceNode>;

	nodes.forEach(({ node }) => {
		services[node.serviceId] = node;
	});

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return services as {
		watchers?: WatchersConfigurationService;
		proforma?: ProformaConfigurationService;
	};
};

export const transformQueryResults = (data: TransformedQueryData, onlyRequiredField = false) => {
	const fieldKeyedById = getFieldsKeyedById(data, onlyRequiredField);

	const { watchers, proforma } = getServicesKeyedByServiceId(data);

	const result: TransformedQueryResult = {
		fieldTabs: getTabLayoutInfo({
			data,
			onlyRequiredField,
			allFieldIds: Object.keys(fieldKeyedById.fieldConfig),
		}),
		...fieldKeyedById,
		meta: {
			...fieldKeyedById.meta,
			queryErrorMessage: getQueryErrorMessage(data),
		},
	};
	result.additionalServices = {
		...(watchers ? { watchersConfigurations: watchers.permissions } : {}),
		...(proforma ? { proformaConfiguration: proforma.proforma } : {}),
		storageLimitsData: data.jira || null,
	};

	return validateRequiredFieldsFromTransformQueryResults(result);
};

const validateRequiredFieldsFromTransformQueryResults = (result: TransformedQueryResult) => {
	const { fieldTabs, fieldConfig } = result;

	const newFieldTabs = fieldTabs.map((tab) => {
		if (!tab.collapsedItems) return tab;

		const collapsedItems = tab.collapsedItems.filter((fieldId) => {
			const field = fieldConfig[fieldId];
			if (field?.isRequired || fieldId === 'summary') {
				return false;
			}
			return true;
		});

		return {
			...tab,
			collapsedItems,
		};
	});

	return {
		...result,
		fieldTabs: newFieldTabs,
	};
};

export const transformQueryData = (data: CreateConfigQuery): TransformedQueryData => ({
	jiraIssueCreateFields: { ...data.jiraIssueCreateFields },
	...(data.jiraIssueCreateAdditionalServices
		? { jiraIssueCreateAdditionalServices: { ...data.jiraIssueCreateAdditionalServices } }
		: {}),
	jiraIssueCreateLayout:
		'jiraIssueCreateLayoutWithML' in data
			? {
					...data.jiraIssueCreateLayoutWithML,
				}
			: { ...data.jiraIssueCreateLayout },
	...(data?.jira ? { jira: { ...data.jira } } : {}),
});

export const tranformSubtaskData = (data: CreateSubtaskConfigQuery): CreateConfigQueryResponse => ({
	jiraIssueCreateFields: { ...data.jiraSubtaskCreationFields },
	jiraIssueCreateAdditionalServices: { ...data.jiraSubtaskCreationAdditionalServices },
	jiraIssueCreateLayout:
		'jiraSubtaskCreationLayoutWithML' in data
			? {
					...data.jiraSubtaskCreationLayoutWithML,
				}
			: { ...data.jiraSubtaskCreationLayout },
	...(data?.jira ? { jira: { ...data.jira } } : {}),
});

export const tranformModalLoadDataInContext = (
	data: CreateConfigQueryInContext,
): CreateConfigQueryResponse => ({
	jiraIssueCreateFields: { ...data.jiraIssueCreateFieldsInContext },
	jiraIssueCreateAdditionalServices: { ...data.jiraIssueCreateAdditionalServices },
	jiraIssueCreateLayout:
		'jiraIssueCreateLayoutInContextWithML' in data
			? {
					...data.jiraIssueCreateLayoutInContextWithML,
				}
			: { ...data.jiraIssueCreateLayoutInContext },
	...(data?.jira ? { jira: { ...data.jira } } : {}),
});
